import React, { useEffect, useState } from "react";
import { useUser } from "util/UserContext";
import Modal from "ui/Modal";
import { Drawer, DrawerContent, DrawerFooter } from "ui/Drawer";
import ActorPrivacy from "components/Common/ActorPrivacy";
import { PrivacyLevel } from "util/userUtils";
import { createUserRelation } from "util/userUtils";
import { toast } from "sonner";

interface DisplayOnProfilePromptProps {
    open: boolean;
    onClose: () => void;
    entityType: "action"; // extendable later
    entityId: number;
    defaultPrivacy: PrivacyLevel;
    children?: React.ReactNode; // preview content supplied by caller
}

const DisplayOnProfilePrompt: React.FC<DisplayOnProfilePromptProps> = ({
    open,
    onClose,
    entityType,
    entityId,
    defaultPrivacy,
    children
}) => {
    const { userIsMobile } = useUser();
    const [selectedPrivacy, setSelectedPrivacy] = useState<PrivacyLevel>(defaultPrivacy);
    const [submitting, setSubmitting] = useState<boolean>(false);

    useEffect(() => {
        setSelectedPrivacy(defaultPrivacy);
    }, [defaultPrivacy, open]);

    const handleSave = async () => {
        try {
            setSubmitting(true);
            await createUserRelation({
                relNodeType: entityType,
                relNodeId: entityId,
                relType: "activity",
                properties: { privacy: selectedPrivacy }
            });
            try { toast.success("Your participation will be displayed on your profile"); } catch { /* ignore */ }
            onClose();
        } catch {
            try { toast.error("Failed to save to profile"); } catch { /* ignore */ }
        } finally {
            setSubmitting(false);
        }
    };

    if (!open) return null;

    const content = (
        <div className="px-1 pb-4">
            <div className="mb-3">
                {children}
            </div>
            <ActorPrivacy
                privacy={selectedPrivacy}
                settingKey="activity_action_privacy"
                actorType="user"
                onPrivacyChange={(p) => setSelectedPrivacy(p)}
                showDropdown
            />
        </div>
    );

    const footer = (
        <div className="flex gap-2 p-4">
            <button
                className="btn-secondary w-full"
                onClick={onClose}
            >
                Cancel
            </button>
            <button
                className="btn-primary w-full"
                disabled={submitting}
                onClick={handleSave}
            >
                Share
            </button>
        </div>
    );

    return userIsMobile ? (
        <Drawer open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DrawerContent className="action-profile-share-drawer" title="Highlight on profile" stickyFooter={true}>
                <div className="px-4 py-2">
                    {content}
                </div>
                <DrawerFooter>
                    <div className="flex gap-2 w-full">
                        <button
                            className="btn-secondary w-full"
                            onClick={onClose}
                        >
                            Cancel
                        </button>
                        <button
                            className="btn-primary w-full"
                            disabled={submitting}
                            onClick={handleSave}
                        >
                            Share
                        </button>
                    </div>
                </DrawerFooter>
            </DrawerContent>
        </Drawer>
    ) : (
        <Modal
            isOpen={open}
            onClose={onClose}
            title="Highlight on profile"
            className="action-profile-share-modal"
            footer={footer}
        >
            {content}
        </Modal>
    );
};

export default DisplayOnProfilePrompt;


